// src/services/payrollService.js
// REPLACE YOUR EXISTING FILE WITH THIS COMPLETE CODE

import { supabase } from './supabase';
import { departmentService } from './departmentService';
import moment from 'moment';

/**
 * Calculate leave days accounting for half-days
 * @param {Object} leave - Leave record with start_date, end_date, leave_duration
 * @returns {Number} Number of days (can be fractional for half-days)
 */
const calculateLeaveDays = (leave) => {
  const start = moment(leave.start_date);
  const end = moment(leave.end_date);
  const fullDays = end.diff(start, 'day') + 1;
  
  // If it's a half-day leave and only one day
  if (leave.leave_duration === 'half_day' && fullDays === 1) {
    return 0.5;
  }
  
  // If it's a half-day leave spanning multiple days
  if (leave.leave_duration === 'half_day' && fullDays > 1) {
    // This assumes first and last days are half-days, middle days are full
    return fullDays - 1 + 0.5;
  }
  
  // Default to full days
  return fullDays;
};

/**
 * Get department-specific leave settings for an employee
 * @param {Object} employee - Employee record
 * @param {String} company_id - Company ID
 * @returns {Object} Department leave settings
 */
const getDepartmentLeaveSettings = async (employee, company_id) => {
  try {
    if (!employee.department) {
      // Return default global settings if no department assigned
      return {
        sick_leave_days: 7,
        local_leave_days: 0
      };
    }

    // Get department by name (since employee.department is stored as text)
    const department = await departmentService.getDepartmentByName(employee.department, company_id);
    
    if (department) {
      return await departmentService.getDepartmentLeaveSettings(department.id, company_id);
    } else {
      // Return default settings if department not found
      return {
        sick_leave_days: 7,
        local_leave_days: 0
      };
    }
  } catch (error) {
    console.error('Error getting department leave settings:', error);
    // Return default settings on error
    return {
      sick_leave_days: 7,
      local_leave_days: 0
    };
  }
};

/**
 * Calculate leave usage and excess for an employee
 * @param {String} employee_id - Employee ID (UUID)
 * @param {Array} leaves - Array of approved leaves for the year
 * @param {Object} departmentSettings - Department leave settings
 * @returns {Object} Leave usage and excess information
 */
const calculateLeaveUsageAndExcess = (employee_id, leaves, departmentSettings) => {
  const employeeLeaves = leaves.filter(leave => leave.employee_id === employee_id);
  
  let sickLeaveUsed = 0;
  let localLeaveUsed = 0;
  let unpaidLeaveUsed = 0;
  let otherLeaveUsed = 0;

  employeeLeaves.forEach(leave => {
    const leaveDays = calculateLeaveDays(leave);
    
    switch (leave.type) {
      case 'sick':
        sickLeaveUsed += leaveDays;
        break;
      case 'local':
        localLeaveUsed += leaveDays;
        break;
      case 'unpaid':
        unpaidLeaveUsed += leaveDays;
        break;
      case 'other':
        otherLeaveUsed += leaveDays;
        break;
    }
  });

   // Calculate excess leave days (only for paid leave types)
  const sickLeaveExcess = Math.max(0, sickLeaveUsed - (departmentSettings.sick_leave_days || 0));
  const localLeaveExcess = Math.max(0, localLeaveUsed - (departmentSettings.local_leave_days || 0));

  // Total excess days (unpaid leave is always deducted, other leave treated as excess)
  const totalExcessDays = sickLeaveExcess + localLeaveExcess + unpaidLeaveUsed + otherLeaveUsed;

  return {
    sickLeaveUsed,
    localLeaveUsed,
    unpaidLeaveUsed,
    otherLeaveUsed,
    sickLeaveExcess,
    localLeaveExcess,
    totalExcessDays,
    sickLeaveRemaining: Math.max(0, (departmentSettings.sick_leave_days || 0) - sickLeaveUsed),
    localLeaveRemaining: Math.max(0, (departmentSettings.local_leave_days || 0) - localLeaveUsed)
  };
};

/**
 * Calculate allowances for an employee in the payroll period
 * @param {String} employee_id - Employee ID (UUID)
 * @param {String} startDate - Period start date
 * @param {String} endDate - Period end date
 * @param {String} company_id - Company ID (UUID)
 * @param {Number} baseGrossPay - Base gross pay (without allowances)
 * @returns {Object} Allowance calculations
 */
const calculateEmployeeAllowances = async (employee_id, startDate, endDate, company_id, baseGrossPay) => {
  try {
    // Get employee allowances that are active during the payroll period
    const { data: allowances, error } = await supabase
      .from('employee_allowances')
      .select(`
        id,
        amount,
        start_date,
        end_date,
        allowance_type_id,
        allowance_types (
          id,
          name,
          is_percentage,
          is_taxable
        )
      `)
      .eq('employee_id', employee_id)
      .eq('company_id', company_id)
      .or(`start_date.lte.${endDate},end_date.gte.${startDate}`);

    if (error) {
      console.error('Error fetching allowances:', error);
      return {
        totalAllowances: 0,
        taxableAllowances: 0,
        nonTaxableAllowances: 0,
        allowanceDetails: []
      };
    }

    let totalAllowances = 0;
    let taxableAllowances = 0;
    let nonTaxableAllowances = 0;
    const allowanceDetails = [];

    (allowances || []).forEach(allowance => {
      // Check if allowance is active during the payroll period
      const allowanceStart = moment(allowance.start_date);
      const allowanceEnd = moment(allowance.end_date);
      const periodStart = moment(startDate);
      const periodEnd = moment(endDate);

      // Check for overlap
      const isActive = allowanceStart.isSameOrBefore(periodEnd) && allowanceEnd.isSameOrAfter(periodStart);
      
      if (isActive) {
        let allowanceAmount = 0;
        
        if (allowance.allowance_types?.is_percentage) {
          // Percentage-based allowance
          allowanceAmount = baseGrossPay * (allowance.amount / 100);
        } else {
          // Fixed amount allowance
          allowanceAmount = allowance.amount;
        }

        totalAllowances += allowanceAmount;

        if (allowance.allowance_types?.is_taxable) {
          taxableAllowances += allowanceAmount;
        } else {
          nonTaxableAllowances += allowanceAmount;
        }

        allowanceDetails.push({
          id: allowance.id,
          name: allowance.allowance_types?.name || 'Unknown',
          amount: allowanceAmount,
          is_percentage: allowance.allowance_types?.is_percentage || false,
          is_taxable: allowance.allowance_types?.is_taxable || true,
          original_amount: allowance.amount
        });
      }
    });

    return {
      totalAllowances,
      taxableAllowances,
      nonTaxableAllowances,
      allowanceDetails
    };
  } catch (error) {
    console.error('Error calculating allowances:', error);
    return {
      totalAllowances: 0,
      taxableAllowances: 0,
      nonTaxableAllowances: 0,
      allowanceDetails: []
    };
  }
};

/**
 * Calculate daily pay with multiple overtime tiers and dynamic settings
 * @param {Object} record - The timesheet record
 * @param {Object} employee - The employee record
 * @param {Object} companySettings - Company settings
 * @param {Array} holidays - Array of holidays
 * @param {Array} leaves - Array of approved leaves
 * @param {Number} totalMonthlyHours - Total hours worked by employee this month
 * @param {Number} monthlyOvertimeThreshold - Monthly threshold for overtime
 * @param {Boolean} isMonthlyOvertimeCalculation - Whether to use monthly overtime calculation
 * @returns {Object} Pay calculation
 */
const calculateDailyPay = async (
  record, 
  employee, 
  companySettings, 
  holidays, 
  leaves, 
  totalMonthlyHours = 0,
  monthlyOvertimeThreshold = 0,
  isMonthlyOvertimeCalculation = false
) => {
  const date = moment(record.date);
  
  // Check if the date is a holiday
  const isHoliday = holidays.some(h => moment(h.date).isSame(date, 'day'));
  
  // Check if it's a weekend based on company settings
  const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, etc.
  const workDays = companySettings?.working_hours?.work_week || [1, 2, 3, 4, 5]; // Default to Mon-Fri
  const isWeekend = !workDays.includes(dayOfWeek.toString());
  
  // Get rates from company settings
  const holidayRateMultiplier = companySettings?.payroll_settings?.holiday_rate || 2.0;
  const overtimeRateMultiplier = companySettings?.payroll_settings?.overtime_rate || 1.5;
  const doubleovertimeRateMultiplier = companySettings?.payroll_settings?.double_overtime_rate || 2.0;
  const regularHoursPerDay = companySettings?.working_hours?.regular_hours || 8;
  const overtimeThreshold = regularHoursPerDay;
  const doubleOvertimeThreshold = companySettings?.payroll_settings?.double_overtime_threshold || (regularHoursPerDay + 2); // Default to 2 hours after regular
  
  let pay = {
    regularHours: 0,
    overtimeHours: 0,
    doubleOvertimeHours: 0,
    holidayHours: 0,
    regularAmount: 0,
    overtimeAmount: 0,
    doubleOvertimeAmount: 0,
    holidayAmount: 0
  };

  // Skip calculation if absent
  if (record.status !== 'present') {
    // Check if this absence is covered by approved leave
    const isOnLeave = checkIfOnApprovedLeave(date, leaves);
    
    // Add leave information to the pay object for reference
    pay.isOnLeave = isOnLeave;
    
    // If it's a weekend or holiday and absent, we don't need to calculate anything
    if (isWeekend || isHoliday) {
      return pay;
    }
    
    return pay;
  }

  // Calculate weekly working hours based on company settings
  const workDaysCount = workDays.length;
  const weeklyHours = workDaysCount * regularHoursPerDay;
  const monthlyHours = weeklyHours * 4; // Approximate month as 4 weeks
  
  // Get hourly rate based on employee salary type
  let hourlyRate;
  
  if (employee.salary_type === 'hourly') {
    hourlyRate = parseFloat(employee.hourly_rate) || 0;
  } else if (employee.salary_type === 'weekly') {
    // Convert weekly salary to hourly based on company's defined work week
    hourlyRate = (parseFloat(employee.weekly_salary) || 0) / weeklyHours;
  } else if (employee.salary_type === 'monthly') {
    // Convert monthly salary to hourly based on company's defined work hours
    hourlyRate = (parseFloat(employee.monthly_salary) || 0) / monthlyHours;
  } else {
    // Default fallback
    hourlyRate = parseFloat(employee.hourly_rate) || 0;
  }

  const totalHours = record.total_hours || 0;

  // Handle holiday and weekend pay - always calculated with special rate regardless of calculation method
  if (isHoliday || isWeekend) {
    pay.holidayHours = totalHours;
    pay.holidayAmount = totalHours * (hourlyRate * holidayRateMultiplier);
    return pay;
  }

  // Handle the two different calculation methods
  if (isMonthlyOvertimeCalculation) {
    // Monthly Overtime Calculation Method
    // First we need to determine if the monthly threshold has been exceeded
    
    // Calculate how many hours from this day would be regular vs overtime
    // If we're already over the monthly threshold, all hours today are overtime
    if (totalMonthlyHours - totalHours >= monthlyOvertimeThreshold) {
      // All hours today are overtime
      pay.overtimeHours = totalHours;
      pay.overtimeAmount = totalHours * (hourlyRate * overtimeRateMultiplier);
    } 
    // If today's hours push us over the threshold, split the hours
    else if (totalMonthlyHours > monthlyOvertimeThreshold) {
      // Calculate how many regular hours remain before overtime
      const regularHoursToday = monthlyOvertimeThreshold - (totalMonthlyHours - totalHours);
      const overtimeHoursToday = totalHours - regularHoursToday;
      
      pay.regularHours = regularHoursToday;
      pay.overtimeHours = overtimeHoursToday;
      
      pay.regularAmount = regularHoursToday * hourlyRate;
      pay.overtimeAmount = overtimeHoursToday * (hourlyRate * overtimeRateMultiplier);
    }
    // If we're still under the threshold even after today's hours
    else {
      // All hours today are regular
      pay.regularHours = totalHours;
      pay.regularAmount = totalHours * hourlyRate;
    }
  } else {
    // Daily Overtime Calculation Method (Original Implementation)
    if (totalHours > doubleOvertimeThreshold) {
      // Calculate hours in each tier
      pay.regularHours = overtimeThreshold;
      pay.overtimeHours = doubleOvertimeThreshold - overtimeThreshold;
      pay.doubleOvertimeHours = totalHours - doubleOvertimeThreshold;
      
      // Calculate amounts for each tier
      pay.regularAmount = overtimeThreshold * hourlyRate;
      pay.overtimeAmount = pay.overtimeHours * (hourlyRate * overtimeRateMultiplier);
      pay.doubleOvertimeAmount = pay.doubleOvertimeHours * (hourlyRate * doubleovertimeRateMultiplier);
    } 
    else if (totalHours > overtimeThreshold) {
      // Only regular and first tier overtime
      pay.regularHours = overtimeThreshold;
      pay.overtimeHours = totalHours - overtimeThreshold;
      
      pay.regularAmount = overtimeThreshold * hourlyRate;
      pay.overtimeAmount = pay.overtimeHours * (hourlyRate * overtimeRateMultiplier);
    } 
    else {
      // Only regular hours
      pay.regularHours = totalHours;
      pay.regularAmount = totalHours * hourlyRate;
    }
  }

  return pay;
};

/**
 * Checks if a given date falls within any approved leaves
 * @param {Object} date - Moment date object
 * @param {Array} leaves - Array of approved leaves
 * @returns {Boolean} Whether the date is covered by approved leave
 */
const checkIfOnApprovedLeave = (date, leaves) => {
  // Find any approved leave that covers this date
  return leaves.some(leave => {
    // Skip if not approved
    if (leave.status !== 'approved') return false;
    
    const leaveStart = moment(leave.start_date);
    const leaveEnd = moment(leave.end_date);
    
    // Check if date falls within leave period
    const isWithinPeriod = date.isSameOrAfter(leaveStart, 'day') && 
                          date.isSameOrBefore(leaveEnd, 'day');
    
    // For half-day leaves, we need to check the specific date
    if (isWithinPeriod && leave.leave_duration === 'half_day') {
      // If it's a single day half-day leave
      if (leaveStart.isSame(leaveEnd, 'day')) {
        return true; // It's a half day, but still counts as on leave
      }
      
      // For multi-day half-day leaves, check if it's the first or last day
      if (date.isSame(leaveStart, 'day') || date.isSame(leaveEnd, 'day')) {
        return true; // It's a half day, but still counts as on leave
      }
      
      // For days in the middle of a half-day leave, it's a full day
      return true;
    }
    
    return isWithinPeriod;
  });
};

export const payrollService = {

  async calculatePayroll(startDate, endDate, company_id) {
    console.log('Starting payroll calculation:', { startDate, endDate, company_id });
    
    try {
      // Fetch company settings for payroll calculations
      const { data: companySettings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', company_id)
        .single();
      
      if (settingsError && settingsError.code !== 'PGRST116') {
        console.error('Settings fetch error:', settingsError);
        throw settingsError;
      }

      // Get holidays for the period
      const { data: holidays, error: holidaysError } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (holidaysError) {
        console.error('Holidays fetch error:', holidaysError);
        throw holidaysError;
      }

      // Get leaves for the current year (for calculating excess leave)
      const currentYear = moment(startDate).year();
      const yearStart = `${currentYear}-01-01`;
      const yearEnd = `${currentYear}-12-31`;

      const { data: yearlyLeaves, error: leavesError } = await supabase
        .from('leaves')
        .select('*')
        .eq('company_id', company_id)
        .eq('status', 'approved') // Only consider approved leaves
        .gte('start_date', yearStart)
        .lte('end_date', yearEnd);

      if (leavesError) {
        console.error('Leaves fetch error:', leavesError);
        throw leavesError;
      }

      // Get leaves for the payroll period (for absence calculations)
      const { data: periodLeaves, error: periodLeavesError } = await supabase
        .from('leaves')
        .select('*')
        .eq('company_id', company_id)
        .eq('status', 'approved') // Only consider approved leaves
        .or(`start_date.gte.${startDate},end_date.gte.${startDate}`)
        .or(`start_date.lte.${endDate},end_date.lte.${endDate}`);

      if (periodLeavesError) {
        console.error('Period leaves fetch error:', periodLeavesError);
        throw periodLeavesError;
      }

      // Get attendance data with correct join relationship
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('timesheet_daily_records')
        .select(`
          *,
          employees (
            id,
            first_name,
            last_name,
            hourly_rate,
            weekly_salary,
            monthly_salary,
            employee_number, 
            position,
            salary_type,
            department
          )
        `)
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (attendanceError) {
        console.error('Attendance fetch error:', attendanceError);
        throw attendanceError;
      }

      // Get list of unique employee IDs
      const employeeIds = [...new Set(attendanceData.map(record => record.employee_id))];

      // Get employee data
      const { data: employees, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .in('id', employeeIds);

      if (employeeError) {
        console.error('Employee fetch error:', employeeError);
        throw employeeError;
      }

      // Create a map of employees by ID for quick lookup
      const employeeMap = {};
      employees.forEach(emp => {
        employeeMap[emp.id] = emp;
      });
      
      // Create payroll period
      const period_id = await this.createPayrollPeriod(startDate, endDate, company_id);

      // Group attendance by employee
      const employeeAttendance = {};
      attendanceData.forEach(record => {
        if (!employeeAttendance[record.employee_id]) {
          employeeAttendance[record.employee_id] = [];
        }
        employeeAttendance[record.employee_id].push(record);
      });

      // Group leaves by employee for leave balance checking
      const employeeLeaves = {};
      if (periodLeaves && periodLeaves.length > 0) {
        periodLeaves.forEach(leave => {
          if (!employeeLeaves[leave.employee_id]) {
            employeeLeaves[leave.employee_id] = [];
          }
          employeeLeaves[leave.employee_id].push(leave);
        });
      }

      // Check if a date is a weekend based on company settings
      const isWeekend = (date) => {
        const dayOfWeek = moment(date).day().toString(); // 0 = Sunday, 1 = Monday, etc.
        const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"]; // Default to Mon-Fri
        return !workDays.includes(dayOfWeek);
      };

      // Get overtime calculation method from settings
      const isMonthlyOvertimeCalculation = 
        companySettings?.payroll_settings?.overtime_calculation_method === 'monthly';
      
      const monthlyOvertimeThreshold = 
        companySettings?.payroll_settings?.monthly_overtime_threshold || 195;

      // Calculate payroll for each employee
      const payrollRecords = await Promise.all(employees.map(async (employee) => {
        const records = employeeAttendance[employee.id] || [];
        const empLeaves = employeeLeaves[employee.id] || [];

        // Get department-specific leave settings
        const departmentLeaveSettings = await getDepartmentLeaveSettings(employee, company_id);
        
        // Calculate yearly leave usage and excess
        const leaveUsage = calculateLeaveUsageAndExcess(employee.id, yearlyLeaves || [], departmentLeaveSettings);

        let regularHours = 0;
        let overtimeHours = 0;
        let doubleOvertimeHours = 0;
        let holidayHours = 0;
        let unpaidLeaveHours = 0;
        
        // Get the overtime thresholds from company settings
        const regularHoursPerDay = companySettings?.working_hours?.regular_hours || 8;
        
        // Get work days in the period
        const workDaysInPeriod = [];
        for (let d = moment(startDate); d.isSameOrBefore(moment(endDate)); d.add(1, 'day')) {
          if (!isWeekend(d) && !holidays.some(h => moment(h.date).isSame(d, 'day'))) {
            workDaysInPeriod.push(d.format('YYYY-MM-DD'));
          }
        }
        
        // Track dates we've already processed (for absence calculations)
        const processedDates = new Set();
        const workingDates = new Set(); // Track dates where employee was actually present

        // Calculate total monthly hours first if using monthly overtime method
        let totalMonthlyHours = 0;
        if (isMonthlyOvertimeCalculation) {
          totalMonthlyHours = records
            .filter(record => record.status === 'present')
            .reduce((sum, record) => sum + (record.total_hours || 0), 0);
        }
        
        // Sort records by date if using monthly calculation
        let sortedRecords = [...records];
        if (isMonthlyOvertimeCalculation) {
          sortedRecords.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
        }
        
        // Process each attendance record
        let runningTotalHours = 0;
        for (const record of sortedRecords) {
          processedDates.add(record.date);

          // Skip calculation for absent records but mark as processed
          if (record.status !== 'present') continue;

          // Track working days (days where employee was actually present)
          workingDates.add(record.date);

          // Calculate daily pay for present records
          const dailyPay = await calculateDailyPay(
            record,
            employee,
            companySettings,
            holidays,
            empLeaves,
            isMonthlyOvertimeCalculation ? runningTotalHours : 0,
            monthlyOvertimeThreshold,
            isMonthlyOvertimeCalculation
          );

          // Update running total before next day's calculation
          runningTotalHours += record.total_hours || 0;

          regularHours += dailyPay.regularHours;
          overtimeHours += dailyPay.overtimeHours;
          doubleOvertimeHours += dailyPay.doubleOvertimeHours;
          holidayHours += dailyPay.holidayHours;
        }
        
        // Now check for absences (dates with no timesheet record)
        for (const workDay of workDaysInPeriod) {
          if (processedDates.has(workDay)) continue; // Already processed
          
          const dayDate = moment(workDay);
          
          // Check if the absence is covered by approved leave
          const isOnApprovedLeave = checkIfOnApprovedLeave(dayDate, empLeaves);
          
          if (!isOnApprovedLeave) {
            // Calculate hours to deduct for this unpaid absence
            let absenceHours = regularHoursPerDay;
            
            // Check if any half-day leaves for this date
            const halfDayLeave = empLeaves.find(leave => 
              leave.leave_duration === 'half_day' && 
              moment(leave.start_date).isSameOrBefore(dayDate, 'day') && 
              moment(leave.end_date).isSameOrAfter(dayDate, 'day')
            );
            
            if (halfDayLeave) {
              absenceHours = regularHoursPerDay / 2; // Only deduct half day
            }
            
            unpaidLeaveHours += absenceHours;
          }
        }

        // Calculate pay amounts based on employee salary type
        let regularAmount = 0;
        let overtimeAmount = 0;
        let doubleOvertimeAmount = 0;
        let holidayAmount = 0;
        let baseGrossPay = 0; // Base pay without allowances
        let leaveExcessDeduction = 0; // Deduction for excess leave

        const overtimeRate = companySettings?.payroll_settings?.overtime_rate || 1.5;
        const doubleOvertimeRate = companySettings?.payroll_settings?.double_overtime_rate || 2.0;
        const holidayRate = companySettings?.payroll_settings?.holiday_rate || 2.0;

        if (employee.salary_type === 'hourly') {
          // Hourly employees get paid based on actual hours worked
          const hourlyRate = parseFloat(employee.hourly_rate) || 0;
          regularAmount = regularHours * hourlyRate;
          overtimeAmount = overtimeHours * (hourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (hourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (hourlyRate * holidayRate);
          
          // Deduct for unpaid leaves
          const unpaidDeduction = unpaidLeaveHours * hourlyRate;
          
          // Calculate excess leave deduction
          leaveExcessDeduction = leaveUsage.totalExcessDays * regularHoursPerDay * hourlyRate;
          
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidDeduction - leaveExcessDeduction;
        } 
        else if (employee.salary_type === 'weekly') {
          // Weekly salary calculation
          const weeklySalary = parseFloat(employee.weekly_salary) || 0;
          const workDaysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
          const workWeeks = workDaysInPeriod / 7;
          
          // Get company work schedule
          const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"]; // Default to Mon-Fri
          const regularHoursPerDay = companySettings?.working_hours?.regular_hours || 8; // Default to 8 hours
          const weeklyHours = workDays.length * regularHoursPerDay;
          
          // Base pay is the weekly salary times number of weeks
          regularAmount = weeklySalary * workWeeks;
          
          // For weekly salary, we add overtime on top using the calculated hourly rate
          const weeklyHourlyRate = weeklySalary / weeklyHours;
          overtimeAmount = overtimeHours * (weeklyHourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (weeklyHourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (weeklyHourlyRate * holidayRate);
          
          // Deduct for unpaid leave days
          const unpaidLeaveDeduction = (unpaidLeaveHours / weeklyHours) * weeklySalary;
          
          // Calculate excess leave deduction
          const dailyRate = weeklySalary / (workDays.length * 5); // Assuming 5 working days per week
          leaveExcessDeduction = leaveUsage.totalExcessDays * dailyRate;
          
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidLeaveDeduction - leaveExcessDeduction;
        } 
        else if (employee.salary_type === 'monthly') {
          // Monthly salary calculation
          const monthlySalary = parseFloat(employee.monthly_salary) || 0;
          
          // Check if we're paying for a full month or partial
          const daysInMonth = moment(endDate).daysInMonth();
          const startOfMonth = moment(startDate).startOf('month').format('YYYY-MM-DD');
          const endOfMonth = moment(endDate).endOf('month').format('YYYY-MM-DD');
          
          // Get company work schedule
          const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"]; // Default to Mon-Fri
          const regularHoursPerDay = companySettings?.working_hours?.regular_hours || 8; // Default to 8 hours
          const weeklyHours = workDays.length * regularHoursPerDay;
          const monthlyHours = weeklyHours * 4; // Approximate hours in a month
          
          // Calculate working days in a month (exclude weekends)
          const workDaysInMonth = Math.floor(daysInMonth * (workDays.length / 7));
          
          let monthRatio = 1;
          
          // If payroll period is not a full month, calculate the ratio
          if (startDate !== startOfMonth || endDate !== endOfMonth) {
            const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
            monthRatio = daysInPeriod / daysInMonth;
          }
          
          // Base pay is the monthly salary adjusted for partial months
          regularAmount = monthlySalary * monthRatio;
          
          // For monthly salary, we add overtime on top using the calculated hourly rate
          const monthlyHourlyRate = monthlySalary / monthlyHours;
          overtimeAmount = overtimeHours * (monthlyHourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (monthlyHourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (monthlyHourlyRate * holidayRate);
          
          // Deduct for unpaid leave days
          const dailyRate = monthlySalary / workDaysInMonth;
          const unpaidLeaveDays = unpaidLeaveHours / regularHoursPerDay; // Convert hours to days
          const unpaidLeaveDeduction = unpaidLeaveDays * dailyRate;
          
          // Calculate excess leave deduction
          leaveExcessDeduction = leaveUsage.totalExcessDays * dailyRate;
          
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidLeaveDeduction - leaveExcessDeduction;
        }

        // Calculate employee allowances
        const allowanceData = await calculateEmployeeAllowances(
          employee.id,
          startDate,
          endDate,
          company_id,
          baseGrossPay
        );

        // Final gross pay includes base pay plus allowances
        const finalGrossPay = baseGrossPay + allowanceData.totalAllowances;

        console.log('Pay calculation:', {
          employeeId: employee.id,
          name: employee.first_name,
          department: employee.department,
          salaryType: employee.salary_type,
          regularHours,
          overtimeHours,
          doubleOvertimeHours,
          holidayHours,
          unpaidLeaveHours,
          regularAmount,
          overtimeAmount,
          doubleOvertimeAmount,
          holidayAmount,
          baseGrossPay,
          allowances: allowanceData,
          finalGrossPay,
          leaveUsage,
          leaveExcessDeduction,
          isMonthlyOvertimeCalculation
        });

        // Build calculation metadata
        const calculationMetadata = {
          method: isMonthlyOvertimeCalculation ? 'monthly' : 'daily',
          working_days: workingDates.size,
          total_records: records.length,
          unique_dates: Array.from(workingDates).sort(),
          overtime_threshold: monthlyOvertimeThreshold,
          calculated_at: new Date().toISOString()
        };

        return {
          company_id,
          period_id,
          employee_id: employee.id,
          regular_hours: regularHours,
          overtime_hours: overtimeHours,
          double_overtime_hours: doubleOvertimeHours,
          holiday_hours: holidayHours,
          unpaid_leave_hours: unpaidLeaveHours,
          regular_amount: regularAmount,
          overtime_amount: overtimeAmount,
          double_overtime_amount: doubleOvertimeAmount,
          holiday_amount: holidayAmount,
          total_allowances: allowanceData.totalAllowances, // NEW: Track total allowances
          allowance_details: JSON.stringify(allowanceData.allowanceDetails), // NEW: Store allowance breakdown
          leave_excess_deduction: leaveExcessDeduction,
          gross_pay: finalGrossPay, // Updated to include allowances
          total_deductions: leaveExcessDeduction, // Start with leave excess as base deduction
          deduction_details: JSON.stringify([]), // Placeholder for recurring deductions (to be implemented)
          net_pay: finalGrossPay - leaveExcessDeduction, // Adjust net pay
          status: 'pending',
          leave_usage: JSON.stringify(leaveUsage),
          calculation_metadata: JSON.stringify(calculationMetadata),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }));

      // Insert payroll records
      if (payrollRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('payroll_records')
          .insert(payrollRecords);

        if (insertError) {
          console.error('Insert error:', insertError);
          throw insertError;
        }

        console.log('Successfully inserted payroll records:', payrollRecords.length);
      }

      return payrollRecords;
    } catch (error) {
      console.error('Detailed payroll calculation error:', error);
      throw error;
    }
  },

  async createPayrollPeriod(startDate, endDate, company_id) {
    const { data, error } = await supabase
      .from('payroll_periods')
      .insert([{
        company_id,
        start_date: startDate,
        end_date: endDate,
        status: 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single();

    if (error) throw error;
    return data.id;
  },

  async recalculatePayrollPeriod(period_id, company_id) {
    console.log('Recalculating payroll for period:', period_id);

    try {
      // Get the existing period dates
      const { data: period, error: periodError } = await supabase
        .from('payroll_periods')
        .select('start_date, end_date')
        .eq('id', period_id)
        .eq('company_id', company_id)
        .single();

      if (periodError) throw periodError;
      if (!period) throw new Error('Payroll period not found');

      const { start_date: startDate, end_date: endDate } = period;

      // Delete existing payroll records for this period
      const { data: deletedRecords, error: deleteError } = await supabase
        .from('payroll_records')
        .delete()
        .eq('period_id', period_id)
        .eq('company_id', company_id)
        .select();

      if (deleteError) throw deleteError;
      console.log(`Deleted ${deletedRecords?.length || 0} existing records for recalculation`);

      // Fetch company settings
      const { data: companySettings, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', company_id)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      // Get holidays for the period
      const { data: holidays, error: holidaysError } = await supabase
        .from('holidays')
        .select('*')
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (holidaysError) throw holidaysError;

      // Get leaves for the current year
      const currentYear = moment(startDate).year();
      const yearStart = `${currentYear}-01-01`;
      const yearEnd = `${currentYear}-12-31`;

      const { data: yearlyLeaves, error: leavesError } = await supabase
        .from('leaves')
        .select('*')
        .eq('company_id', company_id)
        .eq('status', 'approved')
        .gte('start_date', yearStart)
        .lte('end_date', yearEnd);

      if (leavesError) throw leavesError;

      const { data: periodLeaves, error: periodLeavesError } = await supabase
        .from('leaves')
        .select('*')
        .eq('company_id', company_id)
        .eq('status', 'approved')
        .lte('start_date', endDate)
        .gte('end_date', startDate);

      if (periodLeavesError) throw periodLeavesError;

      // Get attendance data
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('timesheet_daily_records')
        .select(`
          *,
          employees (
            id, first_name, last_name, hourly_rate, weekly_salary, monthly_salary,
            employee_number, position, salary_type, department
          )
        `)
        .eq('company_id', company_id)
        .gte('date', startDate)
        .lte('date', endDate);

      if (attendanceError) throw attendanceError;

      const employeeIds = [...new Set(attendanceData.map(record => record.employee_id))];

      const { data: employees, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .in('id', employeeIds);

      if (employeeError) throw employeeError;

      // Group attendance by employee
      const employeeAttendance = {};
      attendanceData.forEach(record => {
        if (!employeeAttendance[record.employee_id]) {
          employeeAttendance[record.employee_id] = [];
        }
        employeeAttendance[record.employee_id].push(record);
      });

      // Group leaves by employee
      const employeeLeaves = {};
      if (periodLeaves && periodLeaves.length > 0) {
        periodLeaves.forEach(leave => {
          if (!employeeLeaves[leave.employee_id]) {
            employeeLeaves[leave.employee_id] = [];
          }
          employeeLeaves[leave.employee_id].push(leave);
        });
      }

      const isWeekend = (date) => {
        const dayOfWeek = moment(date).day().toString();
        const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"];
        return !workDays.includes(dayOfWeek);
      };

      const isMonthlyOvertimeCalculation =
        companySettings?.payroll_settings?.overtime_calculation_method === 'monthly';
      const monthlyOvertimeThreshold =
        companySettings?.payroll_settings?.monthly_overtime_threshold || 195;

      // Calculate payroll for each employee (same logic as calculatePayroll)
      const payrollRecords = await Promise.all(employees.map(async (employee) => {
        const records = employeeAttendance[employee.id] || [];
        const empLeaves = employeeLeaves[employee.id] || [];

        const departmentLeaveSettings = await getDepartmentLeaveSettings(employee, company_id);
        const leaveUsage = calculateLeaveUsageAndExcess(employee.id, yearlyLeaves || [], departmentLeaveSettings);

        let regularHours = 0;
        let overtimeHours = 0;
        let doubleOvertimeHours = 0;
        let holidayHours = 0;
        let unpaidLeaveHours = 0;

        const regularHoursPerDay = companySettings?.working_hours?.regular_hours || 8;

        const workDaysInPeriod = [];
        for (let d = moment(startDate); d.isSameOrBefore(moment(endDate)); d.add(1, 'day')) {
          if (!isWeekend(d) && !holidays.some(h => moment(h.date).isSame(d, 'day'))) {
            workDaysInPeriod.push(d.format('YYYY-MM-DD'));
          }
        }

        const processedDates = new Set();
        const workingDates = new Set();

        let totalMonthlyHours = 0;
        if (isMonthlyOvertimeCalculation) {
          totalMonthlyHours = records
            .filter(record => record.status === 'present')
            .reduce((sum, record) => sum + (record.total_hours || 0), 0);
        }

        let sortedRecords = [...records];
        if (isMonthlyOvertimeCalculation) {
          sortedRecords.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
        }

        let runningTotalHours = 0;
        for (const record of sortedRecords) {
          processedDates.add(record.date);
          if (record.status !== 'present') continue;
          workingDates.add(record.date);

          const dailyPay = await calculateDailyPay(
            record, employee, companySettings, holidays, empLeaves,
            isMonthlyOvertimeCalculation ? runningTotalHours : 0,
            monthlyOvertimeThreshold, isMonthlyOvertimeCalculation
          );

          runningTotalHours += record.total_hours || 0;
          regularHours += dailyPay.regularHours;
          overtimeHours += dailyPay.overtimeHours;
          doubleOvertimeHours += dailyPay.doubleOvertimeHours;
          holidayHours += dailyPay.holidayHours;
        }

        for (const workDay of workDaysInPeriod) {
          if (processedDates.has(workDay)) continue;
          const dayDate = moment(workDay);
          const isOnApprovedLeave = checkIfOnApprovedLeave(dayDate, empLeaves);

          if (!isOnApprovedLeave) {
            let absenceHours = regularHoursPerDay;
            const halfDayLeave = empLeaves.find(leave =>
              leave.leave_duration === 'half_day' &&
              moment(leave.start_date).isSameOrBefore(dayDate, 'day') &&
              moment(leave.end_date).isSameOrAfter(dayDate, 'day')
            );
            if (halfDayLeave) {
              absenceHours = regularHoursPerDay / 2;
            }
            unpaidLeaveHours += absenceHours;
          }
        }

        let regularAmount = 0;
        let overtimeAmount = 0;
        let doubleOvertimeAmount = 0;
        let holidayAmount = 0;
        let baseGrossPay = 0;
        let leaveExcessDeduction = 0;

        const overtimeRate = companySettings?.payroll_settings?.overtime_rate || 1.5;
        const doubleOvertimeRate = companySettings?.payroll_settings?.double_overtime_rate || 2.0;
        const holidayRate = companySettings?.payroll_settings?.holiday_rate || 2.0;

        if (employee.salary_type === 'hourly') {
          const hourlyRate = parseFloat(employee.hourly_rate) || 0;
          regularAmount = regularHours * hourlyRate;
          overtimeAmount = overtimeHours * (hourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (hourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (hourlyRate * holidayRate);
          const unpaidDeduction = unpaidLeaveHours * hourlyRate;
          leaveExcessDeduction = leaveUsage.totalExcessDays * regularHoursPerDay * hourlyRate;
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidDeduction - leaveExcessDeduction;
        } else if (employee.salary_type === 'weekly') {
          const weeklySalary = parseFloat(employee.weekly_salary) || 0;
          const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
          const workWeeks = daysInPeriod / 7;
          const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"];
          const weeklyHours = workDays.length * regularHoursPerDay;
          regularAmount = weeklySalary * workWeeks;
          const weeklyHourlyRate = weeklySalary / weeklyHours;
          overtimeAmount = overtimeHours * (weeklyHourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (weeklyHourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (weeklyHourlyRate * holidayRate);
          const unpaidLeaveDeduction = (unpaidLeaveHours / weeklyHours) * weeklySalary;
          const dailyRate = weeklySalary / (workDays.length * 5);
          leaveExcessDeduction = leaveUsage.totalExcessDays * dailyRate;
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidLeaveDeduction - leaveExcessDeduction;
        } else if (employee.salary_type === 'monthly') {
          const monthlySalary = parseFloat(employee.monthly_salary) || 0;
          const daysInMonth = moment(endDate).daysInMonth();
          const startOfMonth = moment(startDate).startOf('month').format('YYYY-MM-DD');
          const endOfMonth = moment(endDate).endOf('month').format('YYYY-MM-DD');
          const workDays = companySettings?.working_hours?.work_week || ["1", "2", "3", "4", "5"];
          const monthlyHours = workDays.length * regularHoursPerDay * 4;
          const workDaysInMonth = Math.floor(daysInMonth * (workDays.length / 7));
          let monthRatio = 1;
          if (startDate !== startOfMonth || endDate !== endOfMonth) {
            const daysInPeriod = moment(endDate).diff(moment(startDate), 'days') + 1;
            monthRatio = daysInPeriod / daysInMonth;
          }
          regularAmount = monthlySalary * monthRatio;
          const monthlyHourlyRate = monthlySalary / monthlyHours;
          overtimeAmount = overtimeHours * (monthlyHourlyRate * overtimeRate);
          doubleOvertimeAmount = doubleOvertimeHours * (monthlyHourlyRate * doubleOvertimeRate);
          holidayAmount = holidayHours * (monthlyHourlyRate * holidayRate);
          const dailyRate = monthlySalary / workDaysInMonth;
          const unpaidLeaveDays = unpaidLeaveHours / regularHoursPerDay;
          const unpaidLeaveDeduction = unpaidLeaveDays * dailyRate;
          leaveExcessDeduction = leaveUsage.totalExcessDays * dailyRate;
          baseGrossPay = regularAmount + overtimeAmount + doubleOvertimeAmount + holidayAmount - unpaidLeaveDeduction - leaveExcessDeduction;
        }

        const allowanceData = await calculateEmployeeAllowances(
          employee.id, startDate, endDate, company_id, baseGrossPay
        );

        const finalGrossPay = baseGrossPay + allowanceData.totalAllowances;

        const calculationMetadata = {
          method: isMonthlyOvertimeCalculation ? 'monthly' : 'daily',
          working_days: workingDates.size,
          total_records: records.length,
          unique_dates: Array.from(workingDates).sort(),
          overtime_threshold: monthlyOvertimeThreshold,
          recalculated_at: new Date().toISOString()
        };

        return {
          company_id,
          period_id,
          employee_id: employee.id,
          regular_hours: regularHours,
          overtime_hours: overtimeHours,
          double_overtime_hours: doubleOvertimeHours,
          holiday_hours: holidayHours,
          unpaid_leave_hours: unpaidLeaveHours,
          regular_amount: regularAmount,
          overtime_amount: overtimeAmount,
          double_overtime_amount: doubleOvertimeAmount,
          holiday_amount: holidayAmount,
          total_allowances: allowanceData.totalAllowances,
          allowance_details: JSON.stringify(allowanceData.allowanceDetails),
          leave_excess_deduction: leaveExcessDeduction,
          gross_pay: finalGrossPay,
          total_deductions: leaveExcessDeduction,
          deduction_details: JSON.stringify([]),
          net_pay: finalGrossPay - leaveExcessDeduction,
          status: 'pending',
          leave_usage: JSON.stringify(leaveUsage),
          calculation_metadata: JSON.stringify(calculationMetadata),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }));

      // Insert new payroll records
      if (payrollRecords.length > 0) {
        const { error: insertError } = await supabase
          .from('payroll_records')
          .insert(payrollRecords);

        if (insertError) throw insertError;
        console.log('Recalculated and inserted', payrollRecords.length, 'payroll records');
      }

      return payrollRecords;
    } catch (error) {
      console.error('Error recalculating payroll:', error);
      throw error;
    }
  },

  async getPayrollPeriods(company_id) {
    const { data, error } = await supabase
      .from('payroll_periods')
      .select('*')
      .eq('company_id', company_id)
      .order('start_date', { ascending: false });

    if (error) throw error;
    return data;
  },
  
  // Function to get company payroll settings
  async getCompanySettings(company_id) {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('company_id', company_id)
        .single();
        
      if (error && error.code !== 'PGRST116') throw error;
      
      // Create default settings if none exist
      if (!data) {
        return {
          working_hours: {
            regular_hours: 8,
            work_week: ["1", "2", "3", "4", "5"],
            office_start_time: "09:00",
            office_end_time: "17:00",
            grace_period_minutes: 15,
            break_duration_minutes: 60
          },
          payroll_settings: {
            overtime_rate: 1.5,
            double_overtime_rate: 2.0,
            double_overtime_threshold: 10, // After 2 hours of regular overtime
            holiday_rate: 2.0,
            payroll_start_date: 1,
            payroll_end_date: 30,
            enable_auto_processing: false,
            overtime_calculation_method: 'daily', // Default to daily method
            monthly_overtime_threshold: 195      // Default monthly threshold
          },
          leave_settings: {
            annual_leave_days: 14,
            sick_leave_days: 7,
            carry_forward_days: 5,
            auto_approve_leave: false,
            require_attachment_sick_leave: true
          }
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching company settings:', error);
      throw error;
    }
  },

  async getPayrollRecords(period_id, company_id) {
    try {
      const { data, error } = await supabase
      .from('payroll_records')
      .select(`
        id,
        company_id,
        period_id,
        employee_id,
        regular_hours,
        overtime_hours,
        double_overtime_hours,
        holiday_hours,
        unpaid_leave_hours,
        regular_amount,
        overtime_amount,
        double_overtime_amount,
        holiday_amount,
        total_allowances,
        allowance_details,
        leave_excess_deduction,
        gross_pay,
        total_deductions,
        deduction_details,
        net_pay,
        status,
        leave_usage,
        calculation_metadata,
        created_at,
        updated_at,
        employees (
          id,
          first_name,
          last_name,
          employee_number,
          position,
          department,
          salary_type,
          hourly_rate,
          weekly_salary,
          monthly_salary
        )
      `)
      .eq('period_id', period_id)
      .eq('company_id', company_id);

    if (error) {
      console.error('Payroll records fetch error:', error);
      throw error;
    }

    console.log('Fetched payroll records:', data);
    return data;
  } catch (error) {
    console.error('Error getting payroll records:', error);
    throw new Error('Failed to load payroll records');
  }
},

async processDeductions(payroll_record_id, deductions, company_id) {
  try {
    // Begin transaction
    const { data: record, error: recordError } = await supabase
      .from('payroll_records')
      .select('*')
      .eq('id', payroll_record_id)
      .eq('company_id', company_id)
      .single();

    if (recordError) throw recordError;

    // Calculate total deductions (including existing leave excess deduction)
    const additionalDeductions = deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalDeductions = (record.leave_excess_deduction || 0) + additionalDeductions;
    const netPay = record.gross_pay - totalDeductions;

    // Update payroll record
    const { error: updateError } = await supabase
      .from('payroll_records')
      .update({
        total_deductions: totalDeductions,
        net_pay: netPay,
        updated_at: new Date().toISOString()
      })
      .eq('id', payroll_record_id)
      .eq('company_id', company_id);

    if (updateError) throw updateError;

    // Insert deductions
    if (deductions.length > 0) {
      const { error: deductionsError } = await supabase
        .from('employee_deductions')
        .insert(deductions.map(d => ({
          ...d,
          company_id,
          payroll_record_id,
          created_at: new Date().toISOString()
        })));

      if (deductionsError) throw deductionsError;
    }

    return {
      totalDeductions,
      netPay
    };
  } catch (error) {
    console.error('Error processing deductions:', error);
    throw error;
  }
}
};

export default payrollService;